import type { ModalActionHandler } from './modalActionTypes';
import type { Settlement } from '../map/settlement';
import {
    applyCost,
    canAffordCost,
    formatCost,
    getFreePopulation,
    getHouseCapacity,
    removeFreePeople,
} from '../map/settlement';
import { hexDistance } from '../hex/hexMath';
import type { FoundingExpedition } from '../expedition/founding';
import { t } from '../../i18n/translations';

function findSourceSettlement(
    settlements: Settlement[],
    requiredPopulation: number,
): Settlement | null {
    let best = null;
    let bestFree = 0;
    for (const settlement of settlements) {
        const free = getFreePopulation(settlement);
        if (free >= requiredPopulation && free > bestFree) {
            best = settlement;
            bestFree = free;
        }
    }
    return best;
}

export const foundSettlementAction: ModalActionHandler = (state, modal) => {
    if (state.settlementByTileId[modal.tile.id]) return false;
    if (!state.ownedTileIds.has(modal.tile.id)) {
        pushAlert(state, t(state.config.ui.language, 'overlay.alertNotControlled'));
        return true;
    }
    if (state.foundingExpeditions.some((expedition) => expedition.targetTileId === modal.tile.id)) {
        pushAlert(state, t(state.config.ui.language, 'overlay.alertInProgress'));
        return true;
    }
    const requiredPopulation = state.config.initial.settlement.population;
    const housesRequired = Math.ceil(requiredPopulation / getHouseCapacity());
    const settlements = Object.values(state.settlementsById);
    const source = findSourceSettlement(settlements, requiredPopulation);
    if (!source) {
        pushAlert(state, t(state.config.ui.language, 'overlay.alertNoPopulation'));
        return true;
    }

    const { baseTicks, ticksPerHex, foodPerPopPerHex, toolsRequired } = state.config.expedition;
    const sourcePos = parseTileId(source.tileId);
    const targetPos = modal.tile.position;
    const distance = hexDistance(sourcePos, targetPos);
    const foodCost = Math.ceil(distance * requiredPopulation * foodPerPopPerHex);
    const totalTicks = Math.max(1, Math.round(baseTicks + distance * ticksPerHex));
    const cost = mergeCosts(
        { food: foodCost, tools: toolsRequired },
        multiplyCost(state.config.buildings.woodHouse.cost || {}, housesRequired),
    );
    if (!canAffordCost(source, cost)) {
        pushAlert(
            state,
            t(state.config.ui.language, 'overlay.alertNotEnoughResources', formatCost(cost)),
        );
        return true;
    }
    applyCost(source, cost);
    removeFreePeople(source, requiredPopulation);
    state.expeditionIdCounter += 1;
    const expedition: FoundingExpedition = {
        id: `founding_${state.expeditionIdCounter}`,
        fromSettlementId: source.id,
        targetTileId: modal.tile.id,
        population: requiredPopulation,
        foodSupplies: foodCost,
        toolsSupplies: toolsRequired,
        escortUnits: [],
        escortWeaponsIssued: 0,
        cargo: {},
        totalTicks,
        progress: 0,
        distance,
        status: 'traveling',
        createdAt: state.simulation.tick,
    };
    state.foundingExpeditions.push(expedition);
    return true;
};

function multiplyCost(cost: Record<string, number>, multiplier: number): Record<string, number> {
    const scaled: Record<string, number> = {};
    Object.entries(cost).forEach(([key, amount]) => {
        scaled[key] = amount * multiplier;
    });
    return scaled;
}

function mergeCosts(
    base: Record<string, number>,
    extra: Record<string, number>,
): Record<string, number> {
    const merged = { ...base };
    Object.entries(extra).forEach(([key, amount]) => {
        merged[key] = (merged[key] || 0) + amount;
    });
    return merged;
}

function parseTileId(id: string): { q: number; r: number; s: number } {
    const parts = id.split('_');
    if (parts.length >= 4) {
        const q = Number(parts[1]);
        const r = Number(parts[2]);
        const s = Number(parts[3]);
        if (Number.isFinite(q) && Number.isFinite(r) && Number.isFinite(s)) {
            return { q, r, s };
        }
    }
    return { q: 0, r: 0, s: 0 };
}

function pushAlert(state: Parameters<ModalActionHandler>[0], message: string): void {
    state.alertIdCounter += 1;
    state.alerts.push({ id: `alert_${state.alertIdCounter}`, message, level: 'error' });
}
